import { retainedProjectPoints } from '../utils/projectOwnerState';
import { useMemo, useEffect } from 'react';
import { useTaskStore } from '../stores/taskStore';
import { useHabitStore } from '../stores/habitStore';
import { useEventStore } from '../stores/eventStore';
import { useProjectStore } from '../stores/projectStore';
import { useQuestStore } from '../stores/questStore';
import { useAbilitiesStore } from '../stores/abilitiesStore';
import { useBreathworkStore } from '../stores/breathworkStore';
import { useFitnessStore } from '../stores/fitnessStore';
import { useHydrationStore } from '../stores/hydrationStore';
import { useNutritionStore } from '../stores/nutritionStore';
import { useSupplementStore } from '../stores/supplementStore';
import { useMedicationStore } from '../stores/medicationStore';
import { useSleepStore } from '../stores/sleepStore';
import { useRecoveryStore } from '../stores/recoveryStore';
import { useGroomingStore } from '../stores/groomingStore';
import { useHealthStore } from '../stores/healthStore';
import { useSocialStore } from '../stores/socialStore';
import { useCollaborationStore } from '../stores/collaborationStore';
import { useProgramStore } from '../stores/programStore';
import { useRoutineSessionLogStore } from '../stores/routineSessionLogStore';
import { useFinanceStore } from '../stores/financeStore';
import { usePossessionsStore } from '../stores/possessionsStore';
import { useResourcesStore } from '../stores/resourcesStore';
import { useAcquisitionsStore } from '../stores/acquisitionsStore';
import { useIntellectualSubjectsStore } from '../stores/intellectualSubjectsStore';
import { useExistentialPillarsStore } from '../stores/existentialPillarsStore';
import { useAvocationalStore } from '../stores/avocationalStore';
import { CATEGORIES } from '../constants/categories';
import {
  calculateDomainLevelInfo,
  calculateLevelInfo,
  formatPoints,
  calculateDomainStreakBonus,
  getUserRank,
  getDomainRank,
  getBreathingStreakMultiplier,
} from '../utils/levelCalculation';
import { sumEconomyXP } from '../utils/areaRank';
import { sumHydrationXP, toLocalYMD } from '../utils/hydration';
import { useCarePracticeStore } from '../stores/carePracticeStore';
import { sumCareXP } from '../utils/carePractice';
import { sumNutritionXP } from '../utils/nutrition';
import { sumSupplementXP } from '../utils/supplements';
import { sumSleepXP } from '../utils/sleep';
import { sumRecoveryXP } from '../utils/recovery';
import { sumGroomingXP } from '../utils/grooming';
import { computeFinanceEconomyXP } from '../utils/financeXp';
import { computePossessionsEconomyXP } from '../utils/possessionsXp';
import { computeResourcesEconomyXP } from '../utils/resourcesXp';
import { computeAcquisitionsXP } from '../utils/acquisitionsXp';
import { computeIntellectualLearningXP } from '../utils/intellectualXp';
import { computeExistentialDomainXP } from '../utils/existentialXp';
import { computeAvocationalDomainXP } from '../utils/avocationalXp';
import type { UserStats } from '../types';
import { requirementPoints } from '../utils/skillProgress';
import { taskEarnedPoints } from '../utils/taskPoints';

export function useUserStats() {
  const { tasks: regularTasks, setUserLevel: setTaskStoreLevel } = useTaskStore();
  const { habits, setUserLevel: setHabitStoreLevel } = useHabitStore();
  const { events, setUserLevel: setEventStoreLevel } = useEventStore();
  const { projects, setUserLevel: setProjectStoreLevel } = useProjectStore();
  const { quests, setUserLevel: setQuestStoreLevel } = useQuestStore();
  const { abilities, setUserLevel: setAbilitiesStoreLevel, updateDomainLevels } = useAbilitiesStore();
  const { sessions: breathworkSessions, streak: breathworkStreak, setUserLevel: setBreathworkStoreLevel } = useBreathworkStore();
  const fitnessDirectXp = useFitnessStore(s => s.directXp);
  const hydrationHistory = useHydrationStore(s => s.history);
  const nutritionHistory = useNutritionStore(s => s.history);
  const supplementHistory = useSupplementStore(s => s.history);
  const medicationHistory = useMedicationStore(s => s.history);
  const sleepHistory = useSleepStore(s => s.history);
  const sleepTarget = useSleepStore(s => s.targetMinutes);
  const recoveryHistory = useRecoveryStore(s => s.history);
  const groomingHistory = useGroomingStore(s => s.history);
  // ⭐ Health XP INCLUDES the mood/energy check-in again (2026-09-30): the
  // check-in moved into the period tracker, which lives in Health. See `getHealthXP`.
  const healthPoints = useHealthStore(s => s.getHealthXP());
  // Mind's care practices (scope 006) — raw state; the derived sum is in the memo.
  const carePractices = useCarePracticeStore(s => s.practices);
  const careLog = useCarePracticeStore(s => s.log);
  const { socialStats } = useSocialStore();
  const { initiations, programs, setUserLevel: setProgramStoreLevel } = useProgramStore();
  // Completed routine runs carry a per-domain breakdown of their (level-scaled)
  // time-on-task XP. Rolling it in here is what makes routine XP REAL. This is
  // disjoint from the habit/task completions earned inside a run (those credit via
  // their own stores above), so there is no double-counting.
  const routineSessions = useRoutineSessionLogStore(s => s.sessions);
  // Collaboration: XP I earned completing tasks assigned to me in shared projects
  // (0217 server ledger, per category). A member's shared project isn't in their
  // blob, so this is the only path that credits them — folded in exactly like the
  // routine per-domain XP below. assigned_by≠assigned_to + a server-side cap guard it.
  const collaborationXpByCategory = useCollaborationStore(s => s.myXpByCategory);
  // Finance usage → Ecological economy XP (EC3). A derived view of the finance
  // data; the SAME value credits the ecological domain (here) and the Financial
  // Stewardship area (useDisciplines), so the domain is credited exactly once.
  const financeTransactions = useFinanceStore(s => s.transactions);
  const financeBudgets = useFinanceStore(s => s.budgets);
  const financeGoals = useFinanceStore(s => s.goals);
  // Possessions + Resource trackers → Ecological economy XP (EC4/EC5), derived.
  const possessions = usePossessionsStore(s => s.possessions);
  const resources = useResourcesStore(s => s.resources);
  // Time Architecture — derived from applied day templates (EC6).
  const acquisitionItems = useAcquisitionsStore(s => s.items);
  // Intellectual learning XP (IN3) → a derived view of the learning system. Each
  // active Subject's accumulated XP (Learning Sessions + completed resources +
  // manual) credits the Intellectual domain ONCE here — sessions/resources live
  // only in the subject ledger, never the generic stores, so no double-count.
  const intellectualSubjects = useIntellectualSubjectsStore(s => s.subjects);
  // Existential meaning XP (EX3) → the `existential_pillars` ledger sums all three
  // earners (practice activities + Reflections + Quran Mastery); it credits the
  // Existential domain ONCE here, and the radar reads the same ledger.
  const existentialPillars = useExistentialPillarsStore(s => s.pillars);
  // Avocational hobby XP (the 4-layer ledger: Domain ← Path ← Hobby). Each active
  // hobby's earned XP = its play-session XP + the points of the avocational
  // tasks/habits tagged to it (`hobbyId`). It credits the Avocational domain ONCE
  // here; to stay credit-once, hobby-tagged avocational tasks/habits are EXCLUDED
  // from the generic category sums below (they now count via the hobby).
  const avocationalHobbies = useAvocationalStore(s => s.hobbies);

  const stats = useMemo(() => {
    const financeEconomyXP = computeFinanceEconomyXP({
      transactions: financeTransactions.length,
      budgets: financeBudgets.length,
      goals: financeGoals.length,
    });
    const possessionsEconomyXP = computePossessionsEconomyXP(possessions);
    const resourcesEconomyXP = computeResourcesEconomyXP(resources);
    const acquisitionsXP = computeAcquisitionsXP(acquisitionItems);
    // Total Ecological "tracker"/derived XP (finance + possessions + resources +
    // acquisitions) credited to the domain (once).
    const ecologicalTrackerXP =
      financeEconomyXP + possessionsEconomyXP + resourcesEconomyXP + acquisitionsXP;
    // Intellectual learning XP (IN3) → credits the Intellectual domain once.
    const intellectualLearningXP = computeIntellectualLearningXP(intellectualSubjects);
    // Existential meaning XP (EX3) → credits the Existential domain once.
    const existentialMeaningXP = computeExistentialDomainXP(existentialPillars);
    // Avocational hobby XP → credits the Avocational domain once (Σ paths = Σ
    // hobbies). Hobby-tagged avocational tasks/habits are excluded from the generic
    // sums (below) and counted here instead, so it's credit-once.
    const avocTasks = regularTasks['avocational'] ?? [];
    const avocHabits = habits.filter(h => h.category === 'avocational');
    const avocationalHobbyXP = computeAvocationalDomainXP(avocationalHobbies, avocTasks, avocHabits);
    // A task/habit's XP moves into the hobby ledger ONLY when it's tagged to an
    // ACTIVE, still-existing hobby (that's exactly what computeAvocationalDomainXP
    // counts). So we exclude it from the generic sums only under the same condition —
    // a tag to an archived/deleted hobby keeps crediting via the category (no lost XP).
    const activeHobbyIds = new Set(avocationalHobbies.filter(h => h.isActive).map(h => h.id));
    const countsViaHobby = (catId: string, hobbyId?: string) =>
      catId === 'avocational' && !!hobbyId && activeHobbyIds.has(hobbyId);
    // Calculate total points from historical records
    let totalPoints = 0;

    // Add points from tasks and subtasks using their stored point records
    Object.entries(regularTasks).forEach(([catId, categoryTasks]) => {
      categoryTasks.forEach(task => {
        // Hobby-tagged avocational tasks credit via the hobby ledger
        // (avocationalHobbyXP), not here — keeps the domain credit-once.
        if (countsViaHobby(catId, task.hobbyId)) return;
        // Record + completed subtasks + XP kept from replayed rounds.
        totalPoints += taskEarnedPoints(task);
      });
    });

    // Add points from habit completions (hobby-tagged avocational habits credit via
    // the hobby ledger instead — excluded here to stay credit-once).
    habits.forEach(habit => {
      if (countsViaHobby(habit.category, habit.hobbyId)) return;
      totalPoints += (habit.completions?.reduce((sum, c) => sum + c.points, 0) || 0);
    });

    // Add points from completed events
    events.forEach(event => {
      if (event.pointRecord) {
        totalPoints += event.pointRecord.points;
      }
    });

    // Add points from projects
    projects.forEach(project => {
      totalPoints += retainedProjectPoints(project);
      // Add points from completed project tasks
      project.tasks.forEach(task => {
        if (task.pointRecord) {
          totalPoints += task.pointRecord.points;
          
          // Add points from completed sub-tasks
          task.subTasks?.forEach(subTask => {
            if (subTask.pointRecord) {
              totalPoints += subTask.pointRecord.points;
            }
          });
        }
      });

      // Add points from completed projects
      if (project.pointRecord) {
        totalPoints += project.pointRecord.points;
      }
    });

    // Add Ecological tracker XP (finance + possessions) to the user total (once).
    totalPoints += ecologicalTrackerXP;

    // Add Intellectual learning XP (sessions + resources) to the user total (once).
    totalPoints += intellectualLearningXP;

    // Add Existential meaning XP (the pillar ledger) to the user total (once).
    totalPoints += existentialMeaningXP;

    // Add Avocational hobby XP (Σ active hobbies' earned XP) to the user total (once).
    totalPoints += avocationalHobbyXP;

    // Add points from quests
    quests.forEach(quest => {
      // Add points from completed goals
      quest.goals.forEach(goal => {
        if (goal.pointRecord) {
          totalPoints += goal.pointRecord.points;
        }
      });
      
      // Add points from completed quests
      if (quest.pointRecord) {
        totalPoints += quest.pointRecord.points;
      }
    });
    
    // Add points from abilities
    abilities.forEach(ability => {
      // Requirement awards: the kept record, whether or not it is ticked now (RO-A).
      // Skill bonuses stay out of the user total, as before.
      ability.skills.forEach(skill => {
        skill.requirements.forEach(req => {
          totalPoints += requirementPoints(req);
        });
      });
    });

    // Breathwork economy XP — the SAME value credits the user total, the Personal
    // domain (added in categoryStats below), and the Breathing area rank
    // (useDisciplines), so "one action, every ledger" stays consistent.
    const breathingStreakMultiplier = getBreathingStreakMultiplier(breathworkStreak?.currentStreak || 0);
    const breathworkPoints = sumEconomyXP(breathworkSessions, breathingStreakMultiplier);
    totalPoints += breathworkPoints;

    // Hydration economy XP — daily-capped water-log XP. Like breathwork, the SAME
    // value credits the user total, the Personal domain (categoryStats below) and
    // the Nutrition area rank (useDisciplines), so it's "one action, every ledger"
    // with no double-count.
    const hydrationPoints = sumHydrationXP(hydrationHistory);
    totalPoints += hydrationPoints;

    // Nutrition (food-logging) economy XP — same daily-capped, credit-once model;
    // it ADDS INTO the same Nutrition & Hydration area as hydration.
    const nutritionPoints = sumNutritionXP(nutritionHistory);
    totalPoints += nutritionPoints;

    // Direct Fitness Quick-Add XP — credits the Personal domain once. (The param
    // ledger already feeds the Fitness area rank; fitness-tagged habits/programs
    // credit the domain via their own stores, so only these direct adds are here.)
    const fitnessDirectPoints = Math.max(0, fitnessDirectXp || 0);
    totalPoints += fitnessDirectPoints;

    // Supplements adherence XP — daily-capped, credits Personal once + feeds the
    // Supplements area rank (useDisciplines).
    const supplementPoints = sumSupplementXP(supplementHistory);
    totalPoints += supplementPoints;

    // Medicine adherence XP — same daily-capped model; credits Personal once +
    // feeds the HEALTH area rank (useDisciplines).
    const medicationPoints = sumSupplementXP(medicationHistory);
    totalPoints += medicationPoints;

    // Sleep + Recovery adherence XP — daily-capped, credit Personal once + feed
    // their own area ranks (useDisciplines).
    const sleepPoints = sumSleepXP(sleepHistory, sleepTarget);
    totalPoints += sleepPoints;
    const recoveryPoints = sumRecoveryXP(recoveryHistory);
    totalPoints += recoveryPoints;
    const groomingPoints = sumGroomingXP(groomingHistory);
    totalPoints += groomingPoints;

    // Health XP — vitals + the mood/energy check-in (a check-in day earns the full
    // daily cap, a vitals-only day a ratio). Credits Personal once + Health's rank.
    // ⚠️ The Mind area pays nothing here since 2026-09-30 (scope 006, "start
    // fresh"): its clarity check, subsystem acts and Recall were retired, and
    // care practices bring their own term.
    totalPoints += healthPoints;

    // Mind — care practices (scope 006). Derived per practice per day, capped per
    // day (utils/carePractice). ⚠️ Owner decision O2: a practice linked to a habit
    // earns this AND the habit's own XP (counted with habits, in the habit's domain).
    const carePoints = sumCareXP(carePractices, careLog, habits, toLocalYMD(new Date()));
    totalPoints += carePoints;

    // Add Social XP to total
    if (socialStats) {
      totalPoints += socialStats.points;
    }

    // Add points from program module completions
    initiations.forEach(initiation => {
      totalPoints += (initiation.moduleXPEarned || 0);
    });

    // Aggregate routine-run XP per domain (category id → total time-on-task XP across
    // all completed runs). Values are already level-scaled at write, so they're
    // summed raw — matching how program/social XP is added without a streak bonus.
    const routineDomainXP: Record<string, number> = {};
    routineSessions.forEach(session => {
      Object.entries(session.domainXp || {}).forEach(([catId, xp]) => {
        routineDomainXP[catId] = (routineDomainXP[catId] ?? 0) + (xp || 0);
      });
    });
    // Roll the routine XP into the user total.
    totalPoints += Object.values(routineDomainXP).reduce((sum, xp) => sum + xp, 0);
    // …and the collaboration XP (per category, same shape as routine XP).
    totalPoints += Object.values(collaborationXpByCategory).reduce((sum, xp) => sum + xp, 0);

    // Calculate user level based on total points
    const userLevelInfo = calculateLevelInfo(totalPoints);
    const userRank = getUserRank(userLevelInfo.totalLevel);

    // Calculate category stats
    const categoryStats = CATEGORIES.map(category => {
      // Get regular tasks for this category
      const categoryTasks = regularTasks[category.id] || [];
      
      // Get habits for this category
      const categoryHabits = habits.filter(h => h.category === category.id);
      
      // Get events for this category
      const categoryEvents = events.filter(e => e.category === category.id);

      // Get projects for this category
      const categoryProjects = projects.filter(p => p.category === category.id);

      // Get quests for this category
      const categoryQuests = quests.filter(q => q.category === category.id);
      
      // Get abilities for this category
      const categoryAbilities = abilities.filter(a => a.category === category.id);

      // Get social XP for social category
      const socialXP = category.id === 'social' && socialStats ? socialStats.points : 0;

      // Collaboration XP for this category — assigned-task completions in shared
      // projects (0217), keyed by the project's category, same as routine XP below.
      const collaborationXP = collaborationXpByCategory[category.id] ?? 0;

      // Breathwork credits the Personal domain (the Breathing area). Same value
      // already in the user total above — derived once, so no double-count.
      const breathworkXP = category.id === 'personal' ? breathworkPoints : 0;

      // Hydration also credits the Personal domain (the Nutrition area). Same
      // value already in the user total above — derived once, so no double-count.
      const hydrationXP = category.id === 'personal' ? hydrationPoints : 0;
      const nutritionXP = category.id === 'personal' ? nutritionPoints : 0;
      const fitnessDirectXPForCat = category.id === 'personal' ? fitnessDirectPoints : 0;
      const supplementXP = category.id === 'personal' ? supplementPoints : 0;
      const medicationXP = category.id === 'personal' ? medicationPoints : 0;
      const sleepXP = category.id === 'personal' ? sleepPoints : 0;
      const recoveryXP = category.id === 'personal' ? recoveryPoints : 0;
      const groomingXP = category.id === 'personal' ? groomingPoints : 0;
      const healthXP = category.id === 'personal' ? healthPoints : 0;
      const careXP = category.id === 'personal' ? carePoints : 0;
      const ecologicalFinanceXP = category.id === 'ecological' ? ecologicalTrackerXP : 0;
      // Intellectual learning XP credits the Intellectual domain (same value as in
      // the user total above — derived once, no double-count).
      const intellectualLearningForCat = category.id === 'intellectual' ? intellectualLearningXP : 0;
      // Existential meaning XP credits the Existential domain (same value as the
      // user total above — derived once, no double-count).
      const existentialMeaningForCat = category.id === 'existential' ? existentialMeaningXP : 0;
      // Avocational hobby XP credits the Avocational domain (same value as the user
      // total above — derived once). Hobby-tagged tasks/habits are excluded from
      // taskPoints/habitPoints below, so they count here only.
      const avocationalHobbyForCat = category.id === 'avocational' ? avocationalHobbyXP : 0;

      // Calculate Domain Streak Bonus based on active habits
      const streakBonus = calculateDomainStreakBonus(categoryHabits);
      
      // Calculate task points from historical records
      const taskPoints = categoryTasks.reduce((total, task) => {
        // Hobby-tagged avocational tasks credit via the hobby ledger, not here.
        if (countsViaHobby(category.id, task.hobbyId)) return total;
        // Record + completed subtasks + XP kept from replayed rounds.
        const points = taskEarnedPoints(task);
        if (!points) return total;

        // Apply domain streak bonus and breathing streak bonus
        const totalBonus = streakBonus * breathingStreakMultiplier;
        return total + Math.round(points * totalBonus);
      }, 0);
      
      // Calculate habit points from stored completions (hobby-tagged avocational
      // habits credit via the hobby ledger, not here).
      const habitPoints = categoryHabits.reduce((total, habit) => {
        if (countsViaHobby(category.id, habit.hobbyId)) return total;
        const baseHabitPoints = habit.completions?.reduce((sum, c) => sum + c.points, 0) || 0;
        // Apply breathing streak bonus to habit points
        return total + Math.round(baseHabitPoints * breathingStreakMultiplier);
      }, 0);

      // Calculate event points
      const eventPoints = categoryEvents.reduce((total, event) => {
        if (event.pointRecord) {
          // Apply breathing streak bonus to event points
          return total + Math.round(event.pointRecord.points * breathingStreakMultiplier);
        }
        return total;
      }, 0);

      // Calculate project points
      const projectPoints = categoryProjects.reduce((total, project) => {
        let points = retainedProjectPoints(project);

        // Add points from completed tasks and sub-tasks
        project.tasks.forEach(task => {
          if (task.pointRecord) {
            points += task.pointRecord.points;

            // Add points from completed sub-tasks
            task.subTasks?.forEach(subTask => {
              if (subTask.pointRecord) {
                points += subTask.pointRecord.points;
              }
            });
          }
        });

        // Add points from completed project
        if (project.pointRecord) {
          points += project.pointRecord.points;
        }

        // Apply domain streak bonus and breathing streak bonus
        const totalBonus = streakBonus * breathingStreakMultiplier;
        return total + Math.round(points * totalBonus);
      }, 0);

      // Calculate quest points
      const questPoints = categoryQuests.reduce((total, quest) => {
        let points = 0;

        // Add points from completed goals
        quest.goals.forEach(goal => {
          if (goal.pointRecord) {
            points += goal.pointRecord.points;
          }
        });

        // Add points from completed quest
        if (quest.pointRecord) {
          points += quest.pointRecord.points;
        }

        // Apply domain streak bonus and breathing streak bonus
        const totalBonus = streakBonus * breathingStreakMultiplier;
        return total + Math.round(points * totalBonus);
      }, 0);
      
      // Calculate ability points
      const abilityPoints = categoryAbilities.reduce((total, ability) => {
        let points = 0;

        // Requirement awards: the kept record, ticked or not (RO-A).
        ability.skills.forEach(skill => {
          skill.requirements.forEach(req => {
            points += requirementPoints(req);
          });
        });

        // Apply domain streak bonus and breathing streak bonus
        const totalBonus = streakBonus * breathingStreakMultiplier;
        return total + Math.round(points * totalBonus);
      }, 0);

      // Calculate program points for this category
      const categoryPrograms = programs.filter(p => p.category === category.id);
      const programPoints = categoryPrograms.reduce((total, program) => {
        const programInitiations = initiations.filter(i => i.programId === program.id);
        const programXP = programInitiations.reduce((sum, init) => sum + (init.moduleXPEarned || 0), 0);
        return total + programXP;
      }, 0);

      // Routine time-on-task XP credited to this domain (already level-scaled; added
      // raw like programPoints/socialXP, no streak multiplier).
      const routinePoints = routineDomainXP[category.id] ?? 0;

      // Calculate total points for category (add socialXP for social domain,
      // breathworkXP for personal domain)
      const totalCategoryPoints = taskPoints + habitPoints + eventPoints + projectPoints + questPoints + abilityPoints + programPoints + socialXP + routinePoints + breathworkXP + hydrationXP + nutritionXP + fitnessDirectXPForCat + supplementXP + medicationXP + sleepXP + recoveryXP + groomingXP + healthXP + careXP + ecologicalFinanceXP + intellectualLearningForCat + existentialMeaningForCat + avocationalHobbyForCat + collaborationXP;

      // Calculate level info
      const levelInfo = calculateDomainLevelInfo(totalCategoryPoints);
      // Domain rank from the domain ladder (per-domain since 0194) — was
      // getUserRank, which wrongly showed account-rank names per domain.
      const domainRank = getDomainRank(levelInfo.totalLevel, category.id);
      
      return {
        id: category.id,
        name: category.name,
        color: category.color,
        icon: category.icon,
        tasksCompleted: categoryTasks.filter(task => task.completed).length,
        habitsCompleted: categoryHabits.reduce((sum, h) => sum + (h.completedDates?.length || 0), 0),
        eventsCompleted: categoryEvents.filter(e => e.completed).length,
        projectsCompleted: categoryProjects.filter(p => p.status === 'completed').length,
        goalsCompleted: 0,
        points: totalCategoryPoints,
        level: levelInfo.level,
        progress: levelInfo.progress,
        currentPoints: levelInfo.currentPoints,
        pointsForNextLevel: levelInfo.pointsForNextLevel,
        pointsToNextLevel: levelInfo.pointsToNextLevel,
        formattedPoints: formatPoints(totalCategoryPoints),
        streakBonus,
        tier: levelInfo.tier,
        tierLevel: levelInfo.level,
        totalLevel: levelInfo.totalLevel,
        highestRank: domainRank.name
      };
    });

    return {
      totalPoints,
      formattedTotalPoints: formatPoints(totalPoints),
      level: userLevelInfo.level,
      progress: userLevelInfo.progress,
      currentPoints: userLevelInfo.currentPoints,
      pointsToNextLevel: userLevelInfo.pointsToNextLevel,
      categories: categoryStats,
      tier: userLevelInfo.tier,
      tierLevel: userLevelInfo.level,
      totalLevel: userLevelInfo.totalLevel,
      highestRank: userRank.name,
      socialActionsCount: socialStats?.socialActionsCount || 0,
      socialStreakDays: socialStats?.socialStreakDays || 0
    };
  }, [regularTasks, habits, events, projects, quests, abilities, breathworkSessions, breathworkStreak, fitnessDirectXp, hydrationHistory, nutritionHistory, supplementHistory, medicationHistory, sleepHistory, sleepTarget, recoveryHistory, groomingHistory, healthPoints, carePractices, careLog, socialStats, initiations, programs, routineSessions, financeTransactions, financeBudgets, financeGoals, possessions, resources, acquisitionItems, intellectualSubjects, existentialPillars, avocationalHobbies, collaborationXpByCategory]);

  // Update user level in all stores whenever it changes
  useEffect(() => {
    setTaskStoreLevel(stats.level);
    setHabitStoreLevel(stats.level);
    setEventStoreLevel(stats.level);
    setProjectStoreLevel(stats.level);
    setQuestStoreLevel(stats.level);
    setAbilitiesStoreLevel(stats.level);
    setBreathworkStoreLevel(stats.level);
    setProgramStoreLevel(stats.level);
  }, [stats.level, setTaskStoreLevel, setHabitStoreLevel, setEventStoreLevel, setProjectStoreLevel, setQuestStoreLevel, setAbilitiesStoreLevel, setBreathworkStoreLevel, setProgramStoreLevel]);

  // Update domain levels in abilities store whenever domain levels change
  // Only track the actual level values to avoid infinite loops
  const domainLevelsString = useMemo(() =>
    stats.categories.map(c => `${c.id}:${c.totalLevel}`).join(','),
    [stats.categories]
  );

  useEffect(() => {
    updateDomainLevels(stats.categories);
  }, [domainLevelsString, updateDomainLevels]);

  return { stats, userLevel: stats.level };
}